import React from 'react'
import { Box, Typography } from '@mui/material'
import { alpha, useTheme, type SxProps, type Theme } from '@mui/material/styles'
import BusinessIcon from '@mui/icons-material/Business'
import type { CompanyBlockReason } from '../../services/CompanyAccessService'

export const COMPANY_DISABLED_TITLE = 'Company Account Disabled'

export const COMPANY_DISABLED_BODY =
  "You can still sign in, update your profile, see what's new and leave feedback. Everything else in the workspace is unavailable until your company is reactivated. Please try again later or contact your company administrator."

export const COMPANY_DISABLED_AFFECTED_HEADING = "What's affected:"

/** The last line is generic on purpose, so the copy never needs a change per new feature. */
export const COMPANY_DISABLED_AFFECTED_ITEMS = [
  'Dashboard and rooms',
  'Meetings',
  'Chat',
  'Company management',
  'Any other workspace features',
] as const

export const COMPANY_DELETED_LEAD = 'This company workspace no longer exists.'

export interface CompanyDisabledScreenProps {
  companyName: string | null
  reason: CompanyBlockReason | null
  /** `invitation`: shown on /accept-invitation instead of letting the invitee in. */
  variant?: 'workspace' | 'invitation'
  /** Actions under the text, e.g. "Go to sign in" on the invitation page. */
  children?: React.ReactNode
  sx?: SxProps<Theme>
}

/**
 * The one screen a member of a suspended or deleting company sees (SC-409), in place of any
 * section that is not on `SECTIONS_AVAILABLE_WHEN_COMPANY_SUSPENDED`, and on an invitation
 * link for such a company. Never mentions billing: this is an admin suspension.
 */
export default function CompanyDisabledScreen({
  companyName,
  reason,
  variant = 'workspace',
  children,
  sx,
}: CompanyDisabledScreenProps) {
  const theme = useTheme()
  const name = companyName?.trim() || null
  const deleted = reason === 'deleted'

  let lead: React.ReactNode
  if (deleted) {
    lead = COMPANY_DELETED_LEAD
  } else if (variant === 'invitation') {
    lead = (
      <>
        {/* No name when the cloud refused redemption: there is no session to look it up. */}
        This invitation can't be used right now because{' '}
        {name ? <strong>{name}</strong> : 'the company'} is suspended. Please try again later.
      </>
    )
  } else if (name) {
    lead = (
      <>
        <strong>{name}</strong>'s workspace is suspended.
      </>
    )
  } else {
    lead = "Your company's workspace is suspended."
  }

  const showDetails = variant === 'workspace' && !deleted

  return (
    <Box
      className="CompanyDisabledScreen-root"
      data-testid="company-disabled-screen"
      role="alert"
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
          textAlign: 'center',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <BusinessIcon sx={{ fontSize: 64, color: 'warning.main', mb: 3 }} />
      <Typography
        className="CompanyDisabledScreen-title"
        variant="h4"
        component="h1"
        sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}
      >
        {COMPANY_DISABLED_TITLE}
      </Typography>
      <Typography
        className="CompanyDisabledScreen-text"
        variant="body1"
        sx={{ color: 'text.secondary', mb: showDetails ? 1.5 : 3, maxWidth: '500px' }}
      >
        {lead}
      </Typography>

      {showDetails && (
        <>
          <Typography
            className="CompanyDisabledScreen-text"
            variant="body1"
            sx={{ color: 'text.secondary', mb: 3, maxWidth: '500px' }}
          >
            {COMPANY_DISABLED_BODY}
          </Typography>
          <Box
            sx={{
              p: 3,
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              mb: 3,
              maxWidth: '500px',
              width: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: 'warning.main', mb: 2, fontWeight: 600 }}>
              {COMPANY_DISABLED_AFFECTED_HEADING}
            </Typography>
            <Box
              component="ul"
              className="CompanyDisabledScreen-text"
              sx={{ color: 'text.secondary', textAlign: 'left', pl: 3, m: 0, '& li': { mb: 1 } }}
            >
              {COMPANY_DISABLED_AFFECTED_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Box>
          </Box>
        </>
      )}

      {children}
    </Box>
  )
}
